// File Explorer Preferences Management
// Stores user preferences in cookies for persistence across sessions

const COOKIE_NAME = 'file_explorer_prefs'
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60 // 1 year in seconds

interface Preferences {
  sort_by?: string
  sort_direction?: 'asc' | 'desc'
  per_page?: number
}

const DEFAULT_PREFERENCES: Required<Preferences> = {
  sort_by: 'name',
  sort_direction: 'asc',
  per_page: 15
}

/**
 * Load preferences from cookie
 * Returns defaults if cookie doesn't exist or can't be parsed
 */
export function loadPreferences(): Required<Preferences> {
  try {
    const cookieValue = getCookie(COOKIE_NAME)

    if (!cookieValue) {
      return DEFAULT_PREFERENCES
    }

    const preferences = JSON.parse(decodeURIComponent(cookieValue)) as Preferences

    // Merge with defaults to ensure all fields exist
    return {
      sort_by: preferences.sort_by || DEFAULT_PREFERENCES.sort_by,
      sort_direction: preferences.sort_direction || DEFAULT_PREFERENCES.sort_direction,
      per_page: preferences.per_page || DEFAULT_PREFERENCES.per_page
    }
  } catch (error) {
    console.error('Failed to load preferences from cookie:', error)
    return DEFAULT_PREFERENCES
  }
}

/**
 * Save preferences to cookie
 * Merges with existing preferences to preserve other fields
 */
export function savePreferences(preferences: Preferences): void {
  try {
    // Load current preferences and merge with new ones
    const currentPreferences = loadPreferences()
    const updatedPreferences = {
      ...currentPreferences,
      ...preferences
    }

    const cookieValue = encodeURIComponent(JSON.stringify(updatedPreferences))
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    const sameSite = '; SameSite=Lax'

    document.cookie = `${COOKIE_NAME}=${cookieValue}; Max-Age=${COOKIE_MAX_AGE}; Path=/${secure}${sameSite}`
  } catch (error) {
    console.error('Failed to save preferences to cookie:', error)
  }
}

/**
 * Get cookie value by name
 */
function getCookie(name: string): string | null {
  const matches = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
  )
  return matches ? matches[1] : null
}
